import PropTypes from 'prop-types';

import { Flex, token } from '@zillow/constellation';
import { Text } from '@zillow/constellation-10';

import {
  StyledActiveStep,
  StyledCustomStep,
  StyledIconCheckmark,
  StyledInactiveStep,
  StyledStepContainer,
} from './StyledComponents';

const propTypes = {
  index: PropTypes.number, // Passed by ProgressStepper
  activeIndex: PropTypes.number, // Passed by ProgressStepper
  isCompleted: PropTypes.bool, // Passed by ProgressStepper, false by default
  isActive: PropTypes.bool, // Passed by ProgressStepper, false by default
  isWizardComplete: PropTypes.bool, // Passed by ProgressStepper, false by default
  label: PropTypes.string,
  showDesktopViewBreakpoint: PropTypes.oneOf(['md', 'xl', 'xxl']).isRequired,
};

const defaultProps = {
  index: -1,
  activeIndex: -1,
  isCompleted: false,
  isWizardComplete: false,
  isActive: false,
  label: '',
};

const Step = ({
  isCompleted: pIsCompleted,
  isActive: pIsActive,
  isWizardComplete,
  activeIndex,
  index,
  label,
  showDesktopViewBreakpoint,
}) => {
  // set completed if the entire wizard is complete
  const isCompleted = pIsCompleted || isWizardComplete === true;

  // set is active when is active and entire wizard is not complete
  const isActive = pIsActive && isWizardComplete === false;

  const ariaLabel = label ? label.replace(/\s+/g, '-').toLowerCase() : '';

  const renderDot = () => {
    if (isActive) {
      return (
        <StyledActiveStep aria-describedby={`current-step-title-${ariaLabel}`}>
          <title id={`current-step-title-${ariaLabel}`}>Current Step</title>
        </StyledActiveStep>
      );
    }

    const borderColor = index > activeIndex ? token('colors.coolGray400') : token('colors.blue500');
    if (isCompleted) {
      return (
        <StyledCustomStep backgroundColor={borderColor} borderColor={borderColor}>
          <StyledIconCheckmark aria-hidden="false" aria-describedby={`stepper-checkmark-title-${ariaLabel}`}>
            <title id={`stepper-checkmark-title-${ariaLabel}`}>Completed Step</title>
          </StyledIconCheckmark>
        </StyledCustomStep>
      );
    }
    return (
      <StyledInactiveStep aria-describedby={`incomplete-step-title-${ariaLabel}`} borderColor={borderColor}>
        <title id={`incomplete-step-title-${ariaLabel}`}>Incomplete Step</title>
      </StyledInactiveStep>
    );
  };
  return (
    <Flex display="flex" flexDirection="column" alignItems="center">
      <StyledStepContainer isActive={isActive} isCompleted={isCompleted}>
        {renderDot()}
      </StyledStepContainer>
      {/* Only the label is responsive, <Step /> and <ProgressBar /> spacing will adjust if label is hidden */}
      {label && (
        <Text
          textStyle={isActive ? 'body-sm-bold' : 'body-sm'}
          css={{
            textAlign: 'center',
            display: 'none',
            whiteSpace: 'pre',
            sm:
              showDesktopViewBreakpoint === 'sm'
                ? {
                    paddingTop: isActive || isCompleted ? '4px' : '12px',
                    display: 'block',
                  }
                : undefined,
            md:
              showDesktopViewBreakpoint === 'md'
                ? {
                    paddingTop: isActive || isCompleted ? '4px' : '12px',
                    display: 'block',
                  }
                : undefined,
            lg:
              showDesktopViewBreakpoint === 'lg'
                ? {
                    paddingTop: isActive || isCompleted ? '4px' : '12px',
                    display: 'block',
                  }
                : undefined,
            xl:
              showDesktopViewBreakpoint === 'xl'
                ? {
                    paddingTop: isActive || isCompleted ? '4px' : '12px',
                    display: 'block',
                  }
                : undefined,
            xxl:
              showDesktopViewBreakpoint === 'xxl'
                ? {
                    paddingTop: isActive || isCompleted ? '4px' : '12px',
                    display: 'block',
                  }
                : undefined,
          }}
        >
          {label}
        </Text>
      )}
    </Flex>
  );
};

Step.propTypes = propTypes;
Step.defaultProps = defaultProps;

export { Step };
export default Step;
